import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { computePanelLayout } from '@/lib/panel';
import { renderPanelInnerSVG } from '@/lib/svgExport';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';

// Front-panel representation of a device on a detail page (panel view mode).
// Visuals come from the shared SVG renderer (identical to PDF); React Flow
// Handles are overlaid at each port's top/bottom box-edge anchor.
export const PanelDeviceNode = ({ id, data, selected }) => {
  const { language } = useI18n();
  const doc = useStore((s) => s.doc);
  const showBadges = useStore((s) => s.showPortVlanBadges);
  const node = { id, data };
  const L = computePanelLayout(node, { sideHints: data.sideHints });
  const inner = renderPanelInnerSVG(node, doc, language, { showBadges, sideHints: data.sideHints });
  const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${L.width}" height="${L.height}" viewBox="0 0 ${L.width} ${L.height}">${inner.svg}</svg>`;

  return (
    <div className="relative" style={{ width: L.width, height: L.height }} data-testid="panel-device-node">
      <div className="absolute inset-0" dangerouslySetInnerHTML={{ __html: svgStr }} />
      <div
        className="pointer-events-none absolute inset-0 rounded-[9px] transition-shadow duration-150"
        style={{ boxShadow: selected ? '0 0 0 2px #0B5FFF' : 'none' }}
      />
      {Object.entries(L.anchors).map(([portId, a]) => {
        const handleStyle = { width: 7, height: 7, background: '#fff', border: '1.5px solid #1b232c' };
        if (a.dual) {
          // both-sides (patch panel): a TOP and a BOTTOM handle per port at the
          // device edge. The renderer/edge picks the side nearest the connected
          // peer (`__t`/`__b`) to visualise a pass-through.
          return (
            <React.Fragment key={portId}>
              <Handle type="source" position={Position.Top} id={`${portId}__t`} style={{ left: a.anchorX, top: a.topY, transform: 'translate(-50%, -50%)', ...handleStyle }} />
              <Handle type="target" position={Position.Top} id={`${portId}__t`} style={{ left: a.anchorX, top: a.topY, transform: 'translate(-50%, -50%)', ...handleStyle }} />
              <Handle type="source" position={Position.Bottom} id={`${portId}__b`} style={{ left: a.anchorX, top: a.bottomY, transform: 'translate(-50%, -50%)', ...handleStyle }} />
              <Handle type="target" position={Position.Bottom} id={`${portId}__b`} style={{ left: a.anchorX, top: a.bottomY, transform: 'translate(-50%, -50%)', ...handleStyle }} />
            </React.Fragment>
          );
        }
        return (
          <React.Fragment key={portId}>
            <Handle
              type="source"
              position={a.side === 'top' ? Position.Top : Position.Bottom}
              id={portId}
              style={{ left: a.x, top: a.y, transform: 'translate(-50%, -50%)', ...handleStyle }}
            />
            <Handle
              type="target"
              position={a.side === 'top' ? Position.Top : Position.Bottom}
              id={portId}
              style={{ left: a.x, top: a.y, transform: 'translate(-50%, -50%)', ...handleStyle }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};
